import { useCallback } from 'react';
import {
  useCartAddItem,
  useProductInventoryByHandle,
} from '@backpackjs/storefront';

import store, { useRecoilCallback, useGotoRecoilSnapshot } from '@store';
import { useSelectedPlan } from '@hooks';
import { getEncodedId, isEncoded } from '@utils';

export const useAddToCart = ({ selectedVariant, product }) => {
  const gotoSnapshot = useGotoRecoilSnapshot();
  const [selectedPlan] = useSelectedPlan({ selectedVariant });

  const { cartAddItem, ...addItemStatus } = useCartAddItem();
  const { inventory, ...inventoryStatus } = useProductInventoryByHandle({
    handle: product?.handle,
    withQuantity: true,
  });

  // use live inventory data
  const variantStorefrontId = isEncoded(inventory?.id)
    ? getEncodedId(selectedVariant?.storefrontId)
    : selectedVariant?.storefrontId;
  const variantInventory = inventory
    ? inventory?.variants?.[variantStorefrontId]
    : null;

  const GIFT_CARD_HANDLE =
    process.env.APP_GIFT_CARD_HANDLE || 'brumate-e-gift-card';
  const isGiftCard =
    variantInventory?.product?.handle === GIFT_CARD_HANDLE ||
    product?.handle === GIFT_CARD_HANDLE;

  const isSoldOut =
    !variantInventory ||
    (variantInventory.quantityAvailable <= 0 && !isGiftCard);

  const isPreOrder =
    isSoldOut && selectedVariant?.inventoryPolicy === 'CONTINUE';

  const toggleCartSidebar = useRecoilCallback(({ snapshot }) => async () => {
    const release = snapshot.retain();
    try {
      const updatedState = snapshot
        .map(({ set }) => set(store.modal, null))
        .map(({ set }) => set(store.overlay, true))
        .map(({ set }) => set(store.sidebar, 'cart'));

      // update state
      gotoSnapshot(updatedState);
    } finally {
      release();
    }
  });

  // add oneTime and subs
  const addToCart = useCallback(
    async (
      { quantity = 1, callback, openCart = true } = {
        quantity: 1,
        openCart: true,
      }
    ) => {
      if (
        !selectedVariant ||
        (isSoldOut && !isPreOrder) ||
        typeof quantity !== 'number'
      )
        return;

      const item = {
        merchandiseId: selectedVariant.id,
        quantity,
      };

      // adds shipscout attribute to collect data for abtest
      if (typeof window.ShipScoutLineItemProperty !== 'undefined') {
        item.attributes = [
          { key: '_sc', value: window.ShipScoutLineItemProperty },
        ];
      }

      if (typeof window.ShipScoutTrackAddToCart === 'function') {
        window.ShipScoutTrackAddToCart();
      }

      // if selling plan is selected we the subscription
      if (selectedPlan) {
        item.sellingPlanId = selectedPlan.id;
      }

      const cart = await cartAddItem(item);

      if (cart) {
        if (typeof callback === 'function') {
          callback(cart);
        }
        if (openCart) {
          toggleCartSidebar();
        }
      }
    },
    [selectedVariant?.id, isSoldOut, isPreOrder, selectedPlan?.id]
  );

  return [
    {
      isPreOrder,
      isSoldOut,
      selectedPlan,
      status: {
        addItem: addItemStatus,
        inventory,
        inventoryStatus,
      },
    },
    {
      addToCart,
      toggleCartSidebar,
    },
  ];
};
